import React, { useCallback, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Typography,
  TextField,
} from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import { USER_DESCRIPTION_MAP, USER_CUSTOM_QUESTION_MAP, RoutesMap } from 'src/const';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

import QnAImage from 'src/App/assets/undrawMyAnswer.svg';
import { callApi } from 'src/core/callApi';
import { COLOR_GREY_400, COLOR_PRIMARY_1 } from 'src/colors';
import { handleRedirectAfterSignup } from 'src/utils/functions';
import { useHistory } from 'react-router-dom';

const useStyles = makeStyles(theme => ({
  menuItem: {
    whiteSpace: 'normal',
    wordWrap: 'break-word',
    lineHeight: '1.2',
    padding: '12px 16px',
  },
  select: {
    '& .MuiSelect-select': {
      whiteSpace: 'normal',
      wordWrap: 'break-word',
      lineHeight: '1.2',
    },
  },
}));

const UserDescriptionFormOnGoogleAuth = ({ setIsUserDescriptionFormStatusOpen }) => {
  const history = useHistory();
  const classes = useStyles();
  const [userDescription, changeUserDescription] = useState<string>('');
  const [userSubDescription, setUserSubDescription] = useState<string>('');
  const [customUserDescription, setCustomUserDescription] = useState<string>('');
  const [customUserSubDescription, setCustomUserSubDescription] = useState<string>('');
  const [isApiLoading, setIsApiLoading] = useState<boolean>(false);

  // Get the current URL for redirection
  let urlToRedirect = window.location.pathname;

  if (
    urlToRedirect.includes(
      '/airbnb-data/united-kingdom/0/the%20city%20of%20brighton%20and%20hove'
    ) ||
    urlToRedirect.includes('/discovery')
  ) {
    urlToRedirect = '/airbnb-data';
  }

  const handleUserDescriptionChange = useCallback(event => {
    const value = event.target.value;
    changeUserDescription(value);
    setUserSubDescription('');
    setCustomUserSubDescription('');
    if (value !== 'unclassified') {
      setCustomUserDescription('');
    }
  }, []);

  const handleUserSubDescriptionChange = event => {
    const value = event.target.value;
    setUserSubDescription(value);
    if (value !== 'unclassified') {
      setCustomUserSubDescription('');
    }
  };

  const clearLocalStorageFormStatus = () => {
    localStorage.removeItem('user-description-form-to-fill');
  };

  const handleFormSubmit = async () => {
    if (
      !userDescription ||
      (userDescription === 'unclassified' && !customUserDescription.trim()) ||
      (USER_CUSTOM_QUESTION_MAP[userDescription] &&
        (!userSubDescription ||
          (userSubDescription === 'unclassified' && !customUserSubDescription.trim())))
    ) {
      return;
    }

    setIsApiLoading(true);

    const finalUserDescription =
      userDescription === 'unclassified' ? customUserDescription.trim() : userDescription;
    const finalUserSubDescription =
      USER_CUSTOM_QUESTION_MAP[userDescription] && userSubDescription === 'unclassified'
        ? customUserSubDescription.trim()
        : userSubDescription;

    try {
      await callApi(
        'https://8fl0ic90c1.execute-api.us-east-2.amazonaws.com/default/postGoogleAuthDataUpdate',
        {
          body: [
            {
              token: localStorage.getItem('auth-token'),
              user_description: finalUserDescription,
              user_sub_description: finalUserSubDescription,
            },
          ],
          method: 'post',
        },
        true
      );

      clearLocalStorageFormStatus();
      setIsUserDescriptionFormStatusOpen(false);

      // Use the redirection handler
      handleRedirectAfterSignup(
        finalUserDescription,
        finalUserSubDescription,
        history,
        urlToRedirect
      );
    } catch (error) {
      console.error('Error updating user description:', error);
      setIsApiLoading(false);
    }
  };

  return (
    <>
      <Modal open={true}>
        <>
          <Box
            sx={{
              position: 'relative',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 'min(800px, 90vw)',
              boxShadow: 24,
              p: 4,
              background: '#fff',
              borderRadius: '12px',
              outline: 'none',
              border: 'none',
              WebkitBoxShadow: '0 2px 20px -12px #0003',
            }}
          >
            {isApiLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: '#fff7',
                  zIndex: 999999,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <CircularProgress sx={{ color: COLOR_PRIMARY_1 }} />
              </Box>
            )}

            <Box
              sx={{
                position: 'absolute',
                width: '40px',
                height: '40px',
                background: COLOR_GREY_400,
                color: '#fff',
                top: '-20px',
                right: '-20px',
                borderRadius: '900px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
              onClick={() => {
                setIsUserDescriptionFormStatusOpen(false);
                clearLocalStorageFormStatus();
              }}
            >
              <CloseRoundedIcon />
            </Box>
            <Grid container spacing={1} direction="row" alignItems="center" sx={{ width: '100%' }}>
              <Grid item xs={12} sm={4}>
                <img src={QnAImage} style={{ width: '100%', objectFit: 'cover' }} alt="Q&A" />
              </Grid>
              <Grid item xs={12} sm={8}>
                <FormControl variant="outlined" fullWidth required={true}>
                  <Typography
                    variant="h5"
                    sx={{
                      fontSize: '18px',
                      fontWeight: 600,
                      color: '#3AAFA9',
                      marginBottom: '10px',
                    }}
                  >
                    What best describes you?
                  </Typography>
                  <Select
                    labelId="demo-simple-select-outlined-label"
                    id="demo-simple-select-outlined"
                    variant="standard"
                    required
                    fullWidth
                    value={userDescription}
                    onChange={handleUserDescriptionChange}
                    className={classes.select}
                  >
                    {Object.keys(USER_DESCRIPTION_MAP).map(key => (
                      <MenuItem key={key} value={key} className={classes.menuItem}>
                        {USER_DESCRIPTION_MAP[key]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                {userDescription === 'unclassified' && (
                  <TextField
                    onChange={e => setCustomUserDescription(e.target.value)}
                    value={customUserDescription}
                    variant="standard"
                    margin="normal"
                    required
                    fullWidth
                    placeholder="Please describe"
                  />
                )}

                {USER_CUSTOM_QUESTION_MAP[userDescription] && (
                  <FormControl
                    variant="standard"
                    fullWidth
                    required={true}
                    sx={{ marginTop: '20px' }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontSize: '18px',
                        fontWeight: 600,
                        color: '#3AAFA9',
                        marginBottom: '10px',
                      }}
                    >
                      {USER_CUSTOM_QUESTION_MAP[userDescription].question}
                    </Typography>
                    <Select
                      variant="standard"
                      required
                      fullWidth
                      value={userSubDescription}
                      onChange={handleUserSubDescriptionChange}
                      className={classes.select}
                    >
                      {Object.keys(USER_CUSTOM_QUESTION_MAP[userDescription].options).map(key => (
                        <MenuItem key={key} value={key} className={classes.menuItem}>
                          {USER_CUSTOM_QUESTION_MAP[userDescription].options[key]}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {USER_CUSTOM_QUESTION_MAP[userDescription] &&
                  userSubDescription === 'unclassified' && (
                    <TextField
                      onChange={e => setCustomUserSubDescription(e.target.value)}
                      value={customUserSubDescription}
                      variant="standard"
                      margin="normal"
                      required
                      fullWidth
                      placeholder="Please describe"
                    />
                  )}

                {userDescription &&
                  (userDescription !== 'unclassified' || customUserDescription) &&
                  (!USER_CUSTOM_QUESTION_MAP[userDescription] ||
                    (userSubDescription &&
                      (userSubDescription !== 'unclassified' || customUserSubDescription))) && (
                    <Button
                      variant="contained"
                      sx={{
                        maxWidth: '200px',
                        marginTop: '20px',
                        color: '#fff',
                        justifySelf: 'flex-end',
                      }}
                      onClick={handleFormSubmit}
                    >
                      Submit
                    </Button>
                  )}
              </Grid>
            </Grid>
          </Box>
        </>
      </Modal>
    </>
  );
};

export default UserDescriptionFormOnGoogleAuth;
